/**
 * Admin login page mounted at `/admin/login`.
 *
 * Renders an RTL Arabic form with username/password fields. On submit:
 *  - validates field lengths client-side (username 3-50 codepoints,
 *    password 8-128 codepoints) and surfaces inline per-field errors,
 *  - calls `useAuth().login(username, password)` which delegates to
 *    `ApiClient.login` and persists the JWT in `localStorage`,
 *  - navigates to `/admin` on success,
 *  - maps `UnauthorizedError` / status 401 to "بيانات الاعتماد غير صحيحة"
 *    and any other failure to a generic Arabic error message,
 *  - disables the submit button while a login is in flight.
 *
 * Validates: Requirements 4.1, 4.2
 */

import {
  useCallback,
  useState,
  type ChangeEvent,
  type FormEvent,
} from 'react';
import { useNavigate } from 'react-router-dom';

import { ApiError, UnauthorizedError } from '../api/ApiClient';
import { useAuth } from '../auth/AuthContext';

import styles from './AdminLoginPage.module.css';

// ---------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------

const USERNAME_MIN = 3;
const USERNAME_MAX = 50;
const PASSWORD_MIN = 8;
const PASSWORD_MAX = 128;

const USERNAME_LENGTH_ERROR = 'اسم المستخدم يجب أن يكون 3-50 حرفاً';
const PASSWORD_LENGTH_ERROR = 'كلمة المرور يجب أن تكون 8-128 حرفاً';
const INVALID_CREDENTIALS_ERROR = 'بيانات الاعتماد غير صحيحة';
const GENERIC_ERROR = 'حدث خطأ، يرجى المحاولة لاحقاً';

function codepointCount(s: string): number {
  return [...s].length;
}

interface FieldErrors {
  username?: string;
  password?: string;
}

function validate(username: string, password: string): FieldErrors {
  const errors: FieldErrors = {};
  const usernameLen = codepointCount(username);
  const passwordLen = codepointCount(password);
  if (usernameLen < USERNAME_MIN || usernameLen > USERNAME_MAX) {
    errors.username = USERNAME_LENGTH_ERROR;
  }
  if (passwordLen < PASSWORD_MIN || passwordLen > PASSWORD_MAX) {
    errors.password = PASSWORD_LENGTH_ERROR;
  }
  return errors;
}

function isUnauthorized(err: unknown): boolean {
  if (err instanceof UnauthorizedError) return true;
  if (err instanceof ApiError && err.status === 401) return true;
  return false;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function AdminLoginPage(): JSX.Element {
  const navigate = useNavigate();
  const { login } = useAuth();

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleUsernameChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      setUsername(e.target.value);
    },
    [],
  );

  const handlePasswordChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      setPassword(e.target.value);
    },
    [],
  );

  const handleTogglePassword = useCallback(() => {
    setShowPassword((prev) => !prev);
  }, []);

  const handleSubmit = useCallback(
    async (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      if (submitting) return;

      const errors = validate(username, password);
      setFieldErrors(errors);
      setFormError(null);

      if (errors.username !== undefined || errors.password !== undefined) {
        return;
      }

      setSubmitting(true);
      try {
        await login(username, password);
        navigate('/admin', { replace: true });
      } catch (err: unknown) {
        if (isUnauthorized(err)) {
          setFormError(INVALID_CREDENTIALS_ERROR);
        } else {
          setFormError(GENERIC_ERROR);
        }
      } finally {
        setSubmitting(false);
      }
    },
    [submitting, username, password, login, navigate],
  );

  const usernameErrorId = 'admin-login-username-error';
  const passwordErrorId = 'admin-login-password-error';

  return (
    <div className={styles.page} data-testid="admin-login-page">
      <div className={styles.card}>
        {/* Brand / Logo */}
        <div className={styles.brand}>
          <img
            src="/logo.png"
            alt="وصل لي"
            className={styles.brandLogo}
          />
          <h1 className={styles.title}>لوحة الإدارة</h1>
          <p className={styles.brandLabel}>Captain QR Profile</p>
        </div>

        <form
          className={styles.form}
          onSubmit={handleSubmit}
          noValidate
          aria-label="نموذج تسجيل الدخول"
        >
          {/* Username */}
          <div className={styles.field}>
            <label className={styles.label} htmlFor="admin-login-username">
              اسم المستخدم
            </label>
            <div className={styles.inputWrapper}>
              <input
                id="admin-login-username"
                className={
                  fieldErrors.username
                    ? `${styles.input} ${styles.inputError}`
                    : styles.input
                }
                type="text"
                name="username"
                autoComplete="username"
                value={username}
                onChange={handleUsernameChange}
                aria-invalid={fieldErrors.username !== undefined}
                aria-describedby={
                  fieldErrors.username !== undefined ? usernameErrorId : undefined
                }
                disabled={submitting}
                autoFocus
              />
            </div>
            {fieldErrors.username !== undefined && (
              <p
                id={usernameErrorId}
                className={styles.fieldError}
                role="alert"
              >
                {fieldErrors.username}
              </p>
            )}
          </div>

          {/* Password */}
          <div className={styles.field}>
            <label className={styles.label} htmlFor="admin-login-password">
              كلمة المرور
            </label>
            <div className={styles.inputWrapper}>
              <input
                id="admin-login-password"
                className={
                  fieldErrors.password
                    ? `${styles.input} ${styles.inputPassword} ${styles.inputError}`
                    : `${styles.input} ${styles.inputPassword}`
                }
                type={showPassword ? 'text' : 'password'}
                name="password"
                autoComplete="current-password"
                value={password}
                onChange={handlePasswordChange}
                aria-invalid={fieldErrors.password !== undefined}
                aria-describedby={
                  fieldErrors.password !== undefined ? passwordErrorId : undefined
                }
                disabled={submitting}
              />
              <button
                type="button"
                className={styles.togglePassword}
                onClick={handleTogglePassword}
                aria-label={showPassword ? 'إخفاء كلمة المرور' : 'إظهار كلمة المرور'}
                tabIndex={-1}
              >
                {showPassword ? '🙈' : '👁️'}
              </button>
            </div>
            {fieldErrors.password !== undefined && (
              <p
                id={passwordErrorId}
                className={styles.fieldError}
                role="alert"
              >
                {fieldErrors.password}
              </p>
            )}
          </div>

          {formError !== null && (
            <p className={styles.formError} role="alert">
              {formError}
            </p>
          )}

          <button
            type="submit"
            className={styles.submitButton}
            disabled={submitting}
          >
            {submitting ? 'جاري تسجيل الدخول...' : 'تسجيل الدخول'}
          </button>
        </form>
      </div>
    </div>
  );
}
